import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Assignable } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import {
  useMilestoneEdition,
  useMilestoneRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveElementConstraints,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { Modal } from "@common/modal";
import { Loader } from "@common/loader";
import { TagList } from "@common/tagList";
import { ProjectRedirection } from "@common/projectRedirection";
import { DatesPickers } from "@scenes/milestone/datesPickers";
import { Comments } from "@scenes/comments";
import { ElementAttachments } from "@common/elementAttachments";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { Safeguard } from "@scenes/safeguard";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { useModal } from "@root/client/modal";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { Props } from "./types";
import { Details } from "./details";
import { Timestamp } from "./timestamp";
import styles from "./styles.scss";

function Milestone({ milestoneId, isMultiProject, close }: Props) {
  const { t } = useTranslation();
  const {
    isRequestPending,
    isRequestFailure,
    milestone,
    hasRetrievedMilestone,
    retrieveMilestone,
  } = useMilestoneRetrieval(milestoneId);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { pushView } = useModal();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    milestone.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { isRequestPending: isEditRequestPending, editMilestone } =
    useMilestoneEdition(milestone);
  const {
    isRequestPending: isConstraintsRequestPending,
    isRequestSuccess: isConstraintsRequestSuccess,
    isRequestFailure: isConstraintsRequestFailure,
    constraints,
    retrieveElementConstraints,
  } = useRetrieveElementConstraints(milestoneId);

  React.useEffect(() => {
    retrieveMilestone(milestoneId);
  }, [milestoneId]);

  React.useEffect(() => {
    !isConstraintsRequestPending &&
      !isConstraintsRequestSuccess &&
      !isConstraintsRequestFailure &&
      retrieveElementConstraints({
        elementId: milestoneId,
        elementType: "task",
      });
  }, [milestoneId]);

  return (
    <WorkspaceContext type="project" project={milestone.project}>
      <Safeguard
        useCases={[UseCases.editMilestone, UseCases.postComment]}
        context={{ milestone }}
      >
        {({ canPostComment, canEditMilestone }) => (
          <div
            className={classNames(
              { [styles.milestone]: !isRequestPending },
              { [styles.milestoneLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : (
              <Modal
                headerClassName={styles.header}
                closeIconClassName={styles.icon}
                header={
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        onNavigate={close}
                        breadcrumbs={[
                          {
                            name: milestone.project.name,
                            to: `/projects/${milestone.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          {
                            name: milestone.list.name,
                            click: () => {
                              close();
                              pushView({
                                key: UseCases.retrieveList,
                                data: {
                                  listId: milestone.list.uuid,
                                },
                              });
                            },
                          },
                          { name: milestone.name },
                        ]}
                      />
                    </h1>

                    <div
                      className={classNames(styles.rightContent, {
                        [styles.rightContentMultiProject]: isMultiProject,
                      })}
                    >
                      {isMultiProject && (
                        <ProjectRedirection
                          className={styles.contentProjectRedirection}
                          projectId={milestone.project.uuid}
                          close={close}
                        />
                      )}
                      <div>
                        <Timestamp
                          milestone={milestone}
                          project={milestone.project}
                          isMultiProject={isMultiProject}
                        />
                      </div>
                    </div>
                  </div>
                }
                close={close}
              >
                {isRequestFailure ? (
                  <div className={styles.scenePlaceholder}>
                    <ScenePlaceholder goBack={close} />
                  </div>
                ) : (
                  <div className={styles.contentModal}>
                    <div className={styles.main}>
                      <div className={styles.content}>
                        <div className={styles.scrollWrapper}>
                          {constraints.length > 0 && (
                            <div className={styles.constraints}>
                              <ConstraintsPopup
                                direction="right"
                                elementId={milestoneId}
                                elementType="milestone"
                                constraintsNbr={constraints.length}
                                project={milestone.project}
                              />
                              <p className={styles.constraintsText}>
                                {t("milestoneRetrieval.constraint")}
                              </p>
                            </div>
                          )}
                          <div className={styles.details}>
                            <Details
                              milestone={milestone}
                              project={milestone.project}
                              readOnly={!canEditMilestone}
                            />
                          </div>
                          <div className={styles.tagList}>
                            <TagList
                              id={milestone.uuid}
                              tags={milestone.tags}
                              type={Assignable.MILESTONE}
                              project={milestone.project}
                              readOnly={!canEditMilestone}
                            />
                          </div>
                          <Safeguard
                            useCases={[
                              UseCases.editProjectElementDetailsConfiguration,
                            ]}
                            context={{ milestone }}
                          >
                            {({ canEditProjectElementDetailsConfiguration }) =>
                              (template.length > 0 ||
                                canEditProjectElementDetailsConfiguration) && (
                                <div className={styles.secondaryCriteria}>
                                  <div className={styles.row}>
                                    <p className={styles.label}>
                                      {t(
                                        "milestoneRetrieval.secondaryCriteria",
                                      )}
                                    </p>

                                    {canEditProjectElementDetailsConfiguration ? (
                                      <div
                                        className={styles.configurationIconBox}
                                        onClick={() =>
                                          pushView({
                                            key: UseCases.retrieveProjectElementDetailsConfiguration,
                                            data: {
                                              project: milestone.project,
                                            },
                                          })
                                        }
                                      >
                                        <CogWheelIcon
                                          className={styles.configurationIcon}
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className={styles.contextualMenuIcon}
                                      />
                                    )}
                                  </div>
                                  <ProjectElementsSecondaryCriteria
                                    project={milestone.project}
                                    criteriaForms={template.reduce(
                                      (acc, criteria) => ({
                                        ...acc,
                                        [criteria.uuid]: (
                                          <SecondaryCriteriaForm
                                            criteria={criteria}
                                            value={
                                              milestone.attributes
                                                ? milestone.attributes[
                                                    criteria.uuid
                                                  ] || ""
                                                : ""
                                            }
                                            flexible
                                            disabled={isEditRequestPending}
                                            submit={(value) =>
                                              editMilestone({
                                                milestone,
                                                nextMilestone: {
                                                  ...milestone,
                                                  attributes: {
                                                    ...milestone.attributes,
                                                    [criteria.uuid]: value,
                                                  },
                                                },
                                              })
                                            }
                                          />
                                        ),
                                      }),
                                      {},
                                    )}
                                  />
                                </div>
                              )
                            }
                          </Safeguard>
                          <ElementAttachments
                            element={milestone}
                            project={milestone.project}
                            elementType="MILESTONE"
                            readOnly={!canEditMilestone}
                          />
                        </div>
                      </div>
                      <div className={styles.timeline}>
                        <div className={styles.timestamp}>
                          <DatesPickers
                            milestone={milestone}
                            constraints={constraints}
                            readOnly={!canEditMilestone}
                          />
                        </div>
                        <div className={styles.comments}>
                          {!milestone.project.isTemplate && (
                            <Comments
                              subject={milestone.uuid}
                              type="milestone"
                              canPost={canPostComment}
                              project={milestone.project.uuid}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Modal>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { Milestone };
